import React, { useEffect, useState } from 'react';
import { storyBibleApi } from '../../api/storyBible';
import SamePersonMerge from './SamePersonMerge';
import { parsePersonLabel, personFocusNames } from '../../utils/personNames';
import { detectInflightKey, sharedDetectInflight } from '../../utils/detectInflight';
import './BookSheet.css';

// Book Sheet Cards Increment 2 — the spec's "full pattern transfer" of
// Character's detect-and-pick step onto Setting/Location. Same wizard,
// parametrized by category rather than a second component, since the two
// only differ in which detect endpoint they call and their copy strings.
const WIZARD_CONFIG = {
  Character: {
    detect: (storyId) => storyBibleApi.detectCharacters(storyId),
    noun: 'characters',
    manualLabel: 'Character name',
    scanFailureMessage: 'Could not scan prior sessions for characters.',
  },
  'Setting/Location': {
    detect: (storyId) => storyBibleApi.detectLocations(storyId),
    noun: 'locations',
    manualLabel: 'Setting/Location name',
    scanFailureMessage: 'Could not scan prior sessions for locations.',
  },
};

/**
 * NamingWizard — Book Sheet Cards Part 6, the "New Character"/"New
 * Setting/Location" picker.
 *
 * Pre-creation: scans prior sessions (read-only) and shows an ordered
 * checkbox candidate list. Checking a candidate without supplying a real
 * name creates that card with `unnamed_flagged: true` (Part 6 step 5) — the
 * optional rename field next to a checked row is how the writer supplies a
 * real name at pick time instead. A separate "type a name" field creates an
 * entirely new, always-named draft not on the detected list.
 *
 * Confirming does NOT persist anything — add-cards requires a non-empty
 * description and these drafts have none yet. It hands back an array of
 * {instanceName, unnamedFlagged} drafts; the caller (BookSheet) opens each
 * as an unsaved card in the detail panel, where the normal
 * generate → approve flow (Part 3/4) takes over and is what actually
 * persists the row.
 */
export default function NamingWizard({ storyId, category, onCancel, onConfirm }) {
  const config = WIZARD_CONFIG[category] || WIZARD_CONFIG.Character;
  const [candidates, setCandidates] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [checked, setChecked] = useState(() => new Set());
  const [renames, setRenames] = useState({}); // entity_id -> text
  const [manualName, setManualName] = useState('');
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setCandidates(null);
    setChecked(new Set());
    setRenames({});
    setManualName('');
    // Join an identical in-flight scan instead of starting a second
    // paid extract. Unmount does not abort — the request is not
    // cancelable without orphaning work the next open would re-pay.
    sharedDetectInflight(detectInflightKey(storyId, category), () => config.detect(storyId))
      .then((data) => {
        if (!cancelled) setCandidates(data?.candidates || []);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e.message || config.scanFailureMessage);
          setCandidates([]);
        }
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyId, category]);

  const toggleChecked = (id) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const hasSelection = checked.size > 0 || manualName.trim().length > 0;

  const draftFromCandidate = (candidate, rename) => {
    const parsed = parsePersonLabel(candidate.name);
    const primary = rename || candidate.primary_name || parsed.name || candidate.name;
    const nick = candidate.nickname || parsed.nickname || '';
    return {
      instanceName: primary,
      nickname: nick,
      alsoKnownAs: personFocusNames({
        name: primary,
        nickname: nick,
        alsoKnownAs: candidate.also_known_as || [parsed.name, nick],
      }),
      unnamedFlagged: !rename,
    };
  };

  const handleConfirm = () => {
    const drafts = [];
    for (const candidate of candidates || []) {
      if (!checked.has(candidate.entity_id)) continue;
      const rename = (renames[candidate.entity_id] || '').trim();
      drafts.push(draftFromCandidate(candidate, rename));
    }
    const manual = manualName.trim();
    if (manual) drafts.push({ instanceName: manual, nickname: '', alsoKnownAs: [manual], unnamedFlagged: false });
    if (drafts.length === 0) return;
    onConfirm(drafts);
  };

  const handleOfferMerge = (result) => {
    const keep = (candidates || []).find((c) => c.entity_id === result.keepId);
    const absorb = (candidates || []).find((c) => c.entity_id === result.absorbId);
    if (!keep || !absorb) {
      setMerging(false);
      return;
    }
    const merged = {
      ...keep,
      name: result.label,
      primary_name: result.name,
      nickname: result.nickname,
      also_known_as: result.alsoKnownAs,
      mention_count: (keep.mention_count || 0) + (absorb.mention_count || 0),
    };
    setCandidates((prev) => (prev || [])
      .filter((c) => c.entity_id !== absorb.entity_id)
      .map((c) => (c.entity_id === keep.entity_id ? merged : c)));
    setChecked((prev) => {
      const next = new Set(prev);
      next.delete(absorb.entity_id);
      next.add(keep.entity_id);
      return next;
    });
    setMerging(false);
  };

  return (
    <>
    <div className="bs-modal-backdrop" onClick={onCancel}>
      <div className="bs-modal" role="dialog" aria-modal="true" aria-label={`New ${category}`} onClick={(e) => e.stopPropagation()}>
        <div className="bs-modal__header">
          <h2>New {category}</h2>
          <button className="bs-modal__close" onClick={onCancel} aria-label="Close">&times;</button>
        </div>

        <p className="bs-modal__intro">
          Pick from {config.noun} noticed in your sessions, or type a name directly.
          Nothing is created for names you don't select.
        </p>

        {error && <div className="bs-error">{error}</div>}

        {candidates === null ? (
          <div className="bs-modal__state">Scanning prior sessions…</div>
        ) : candidates.length === 0 ? (
          <div className="bs-modal__state">
            No {config.noun} noticed in this story's sessions yet — type a name below.
          </div>
        ) : (
          <ul className="bs-wizard-list" role="group" aria-label={`Detected ${config.noun}`}>
            {candidates.map((c) => (
              <li key={c.entity_id} className="bs-wizard-row">
                <label className="bs-wizard-row__check">
                  <input
                    type="checkbox"
                    checked={checked.has(c.entity_id)}
                    onChange={() => toggleChecked(c.entity_id)}
                  />
                  <span className="bs-wizard-row__name">{c.name}</span>
                  <span className="bs-wizard-row__mentions">{c.mention_count} mention{c.mention_count === 1 ? '' : 's'}</span>
                </label>
                {checked.has(c.entity_id) && (
                  <input
                    type="text"
                    className="bs-wizard-row__rename"
                    placeholder={`Full name (optional — leave blank to keep calling them ${c.name})`}
                    value={renames[c.entity_id] || ''}
                    onChange={(e) => setRenames((prev) => ({ ...prev, [c.entity_id]: e.target.value }))}
                  />
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="bs-wizard-manual">
          <label className="bs-edit__label">Or type a new name</label>
          <input
            type="text"
            className="bs-edit__input"
            value={manualName}
            onChange={(e) => setManualName(e.target.value)}
            placeholder={config.manualLabel}
          />
        </div>

        {category === 'Character' && (candidates || []).length >= 2 && (
          <button type="button" className="bs-add-link" onClick={() => setMerging(true)}>
            These are the same person…
          </button>
        )}

        <div className="bs-modal__actions">
          <button className="bs-btn bs-btn--primary" onClick={handleConfirm} disabled={!hasSelection}>
            Create note{checked.size + (manualName.trim() ? 1 : 0) > 1 ? 's' : ''}
          </button>
          <button className="bs-btn bs-btn--secondary" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
    {merging && (
      <SamePersonMerge
        items={(candidates || []).map((c) => ({
          id: c.entity_id,
          label: c.name,
          primaryName: c.primary_name || parsePersonLabel(c.name).name,
          nickname: c.nickname || parsePersonLabel(c.name).nickname,
          alsoKnownAs: c.also_known_as || [],
        }))}
        onCancel={() => setMerging(false)}
        onConfirm={handleOfferMerge}
      />
    )}
    </>
  );
}
